import {
  Box,
  Breadcrumbs,
  Inline,
  Skeleton,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import { useInvoiceReport } from '@omnipresentgroup/domains';
import { useNavigate, useParams } from 'react-router';

import { INVOICES_PATH } from '../../../constants';
import { InvoiceReportEmployeeList } from '../../../features/invoice-reports';
import { useManagerUser } from '../../../hooks';
import { INVOICE_REPORTS_LIST_PAGE_TITLE } from '../InvoiceReports';
import { InvoiceReportActions, InvoiceReportSummary } from './components';

const InvoiceReportDetailsSkeleton = () => (
  <Stack>
    <Inline gap="16" justify="space-between">
      <Box w={150}>
        <Skeleton height={40} />
      </Box>
      <Box w="100%">
        <Skeleton height={40} />
      </Box>
    </Inline>
    <Skeleton height={60} />
    <Skeleton height={60} />
  </Stack>
);

export const InvoiceReportDetails = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();

  const navigate = useNavigate();

  const { companyId, userId } = useManagerUser();

  const { data: invoice, isLoading: isInvoiceLoading } = useInvoiceReport({
    companyId,
    invoiceId: invoiceId || '',
    enabled: !!invoiceId,
  });

  return (
    <Stack gap="16">
      <Breadcrumbs
        crumbs={[
          { id: 'home', title: 'Home', onClick: () => navigate('/') },
          {
            id: 'invoices',
            title: INVOICE_REPORTS_LIST_PAGE_TITLE,
            onClick: () => navigate(INVOICES_PATH),
          },
          {
            id: 'invoice',
            title: invoice?.id ?? '',
            active: true,
          },
        ]}
      />

      {invoiceId && (
        <Stack gap="24">
          {isInvoiceLoading && !invoice && <InvoiceReportDetailsSkeleton />}

          {invoice && (
            <Stack gap="32">
              <Inline
                minH={40}
                align="center"
                justify={'space-between'}
                css={{ flexWrap: 'wrap' }}
              >
                <Stack gap="4">
                  <Typography
                    as="h3"
                    hideParagraphSpacing
                    size="24"
                    weight="medium"
                  >
                    {invoice.id}
                  </Typography>
                  <Typography as="span" size="16" color="subtle">
                    {invoice.reference}
                  </Typography>
                </Stack>
                <InvoiceReportActions invoice={invoice} />
              </Inline>
              <InvoiceReportSummary invoice={invoice} />
            </Stack>
          )}

          {invoice && (
            <InvoiceReportEmployeeList
              companyId={companyId}
              invoice={invoice}
              userId={userId}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};
